import { createBookSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiList, api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, Pager, errorMessage, primaryButton } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { parseMajor } from '../../lib/money';

interface Book {
  id: string;
  title: string;
  author: string;
  isbn: string | null;
  category: string | null;
  totalCopies: number;
  availableCopies: number;
}

export function CatalogueTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({
    title: '',
    author: '',
    isbn: '',
    category: '',
    price: '',
    copies: '1',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const school = useQuery({
    queryKey: ['schools', schoolId],
    queryFn: () => api<{ currencyCode: string }>(`/schools/${schoolId}`),
  });
  const currency = school.data?.currencyCode ?? 'USD';
  const books = useQuery({
    queryKey: ['library', schoolId, 'books', q, availableOnly, page],
    queryFn: () =>
      apiList<Book>(`/schools/${schoolId}/library/books`, {
        query: { q, availableOnly: availableOnly ? 'true' : undefined, page, pageSize: 20 },
      }),
  });
  const add = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/library/books`, { method: 'POST', body }),
    onSuccess: () => {
      setForm({ title: '', author: '', isbn: '', category: '', price: '', copies: '1' });
      setMessage({ kind: 'ok', text: t('lib.bookAdded') });
      void queryClient.invalidateQueries({ queryKey: ['library', schoolId] });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  return (
    <section aria-label={t('lib.tab.catalogue')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <div className="flex flex-wrap items-end gap-4">
        <TextField
          className="min-w-64 flex-1"
          label={t('lib.search')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={availableOnly}
            onChange={(e) => {
              setAvailableOnly(e.target.checked);
              setPage(1);
            }}
          />
          {t('lib.availableOnly')}
        </label>
      </div>

      {books.data?.data.length === 0 && <p className="text-slate-600">{t('lib.noBooks')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {books.data?.data.map((b) => (
          <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
            <span>
              <span className="font-medium">{b.title}</span> · {b.author}
              {b.category ? (
                <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs">{b.category}</span>
              ) : null}
              {b.isbn ? (
                <span className="ml-2 font-mono text-xs text-slate-500">{b.isbn}</span>
              ) : null}
            </span>
            <span className={b.availableCopies > 0 ? 'text-green-800' : 'text-red-700'}>
              {t('lib.copies', { available: b.availableCopies, total: b.totalCopies })}
            </span>
          </li>
        ))}
      </ul>
      {books.data && books.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={books.data.meta.totalPages} onPage={setPage} />
      )}

      {can('library.manage', schoolId) && (
        <form
          noValidate
          aria-label={t('lib.addBook')}
          onSubmit={(e) => {
            e.preventDefault();
            const priceMinor = form.price.trim() === '' ? null : parseMajor(form.price, currency);
            const parsed = createBookSchema.safeParse({
              title: form.title,
              author: form.author,
              isbn: form.isbn,
              category: form.category,
              priceMinor,
              copies: Number(form.copies),
            });
            const next: Record<string, string> = {};
            if (!parsed.success)
              for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
            if (form.price.trim() !== '' && priceMinor === null) next.priceMinor = t('lib.invalid');
            setErrors(next);
            if (!parsed.success || Object.keys(next).length > 0) return;
            setMessage(null);
            add.mutate(parsed.data);
          }}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
        >
          <TextField
            label={t('lib.bookTitle')}
            value={form.title}
            error={errors.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <TextField
            label={t('lib.author')}
            value={form.author}
            error={errors.author}
            onChange={(e) => setForm({ ...form, author: e.target.value })}
          />
          <TextField
            label={t('lib.isbn')}
            value={form.isbn}
            error={errors.isbn}
            onChange={(e) => setForm({ ...form, isbn: e.target.value })}
          />
          <TextField
            label={t('lib.category')}
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
          <TextField
            label={`${t('lib.price')} (${currency})`}
            inputMode="decimal"
            value={form.price}
            error={errors.priceMinor}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
          />
          <TextField
            label={t('lib.copiesToAdd')}
            type="number"
            value={form.copies}
            error={errors.copies}
            onChange={(e) => setForm({ ...form, copies: e.target.value })}
          />
          <div>
            <button type="submit" className={primaryButton} disabled={add.isPending}>
              {t('lib.addBook')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
